import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquare,
  faSquareCheck,
  faChevronRight,
  faChevronDown,
  faPlusSquare,
  faMinusSquare,
} from "@fortawesome/pro-regular-svg-icons";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import SearchField from "../../common/SearchField";
import SmallLoader from "../../loaders/SmallLoader";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import {
  applySearchAwareTreeCheckChange,
  filterCheckboxTreeBySearch,
  flattenCheckboxTreeToValues,
  normalizeCheckboxTreeNodesForDisplay,
  sortCheckboxTreeNodes,
} from "../../../helpers/searchableFilterChecklist";

const DEFAULT_TREE_MAX_HEIGHT = 280;
const SEARCH_DEBOUNCE_MS = 400;

const treeIcons = {
  check: <FontAwesomeIcon className="text-black" icon={faSquareCheck} />,
  uncheck: <FontAwesomeIcon className="text-black" icon={faSquare} />,
  halfCheck: <FontAwesomeIcon className="text-black" icon={faSquareCheck} />,
  expandClose: (
    <FontAwesomeIcon className="text-black" icon={faChevronRight} />
  ),
  expandOpen: <FontAwesomeIcon className="text-black" icon={faChevronDown} />,
  expandAll: <FontAwesomeIcon className="text-black" icon={faPlusSquare} />,
  collapseAll: <FontAwesomeIcon className="text-black" icon={faMinusSquare} />,
  parentClose: <span />,
  parentOpen: <span />,
  leaf: <span />,
};

interface LocationScopeSearchableTreeProps {
  nodes?: CheckboxTreeNodeDto[];
  checked: string[];
  expanded: string[];
  onCheckedChange: (checked: string[]) => void;
  onExpandedChange: (expanded: string[]) => void;
  isLoading: boolean;
  loadingMessage?: string;
  /** Max height of the scrollable tree (px). Default 280. */
  listMaxHeight?: number;
}

function LocationScopeSearchableTree({
  nodes,
  checked,
  expanded,
  onCheckedChange,
  onExpandedChange,
  isLoading,
  loadingMessage = "Loading...",
  listMaxHeight = DEFAULT_TREE_MAX_HEIGHT,
}: LocationScopeSearchableTreeProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const lastExpandKeyRef = useRef("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const sortedNodes = useMemo(
    () => sortCheckboxTreeNodes(nodes),
    [nodes]
  );

  const allValues = useMemo(
    () => flattenCheckboxTreeToValues(sortedNodes),
    [sortedNodes]
  );

  const { filteredNodes, visibleValues, expandValues } = useMemo(
    () => filterCheckboxTreeBySearch(sortedNodes, debouncedSearchQuery),
    [sortedNodes, debouncedSearchQuery]
  );

  const displayNodes = useMemo(
    () => normalizeCheckboxTreeNodesForDisplay(filteredNodes),
    [filteredNodes]
  );

  const hasSearch = debouncedSearchQuery.length > 0;
  const isSearchPending =
    searchInput.trim() !== debouncedSearchQuery;
  const selectedCount = checked.length;

  useEffect(() => {
    if (!hasSearch) {
      lastExpandKeyRef.current = "";
      return;
    }
    const expandKey = expandValues.join("|");
    if (expandKey === lastExpandKeyRef.current) return;
    lastExpandKeyRef.current = expandKey;
    onExpandedChange(expandValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-expand when search results change
  }, [hasSearch, expandValues.join("|")]);

  const handleSelectAllInList = () => {
    if (hasSearch) {
      const combined = Array.from(
        new Set([...checked.map(String), ...visibleValues.map(String)])
      );
      onCheckedChange(combined);
    } else {
      onCheckedChange([...allValues]);
    }
  };

  const handleClearAll = () => {
    if (hasSearch) {
      const visibleSet = new Set(visibleValues.map(String));
      onCheckedChange(checked.filter((id) => !visibleSet.has(String(id))));
    } else {
      onCheckedChange([]);
    }
  };

  const handleCheck = (
    libraryChecked: string[],
    targetNode: { value: string }
  ) => {
    if (!hasSearch) {
      onCheckedChange(libraryChecked);
      return;
    }

    const wasChecked = checked.map(String).includes(String(targetNode.value));
    onCheckedChange(
      applySearchAwareTreeCheckChange({
        prevChecked: checked,
        libraryChecked,
        targetValue: targetNode.value,
        fullTree: sortedNodes,
        filteredNodes,
        visibleValues,
        isChecking: !wasChecked,
      })
    );
  };

  if (isLoading) {
    return (
      <div className="pt-2">
        <SmallLoader />
        <p className="text-center pt-1 text-[#959595] text-sm">
          {loadingMessage}
        </p>
      </div>
    );
  }

  if (!nodes?.length) {
    return (
      <div className="pt-2">
        <p className="text-sm text-gray-500 text-center py-3">
          No locations available.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-2">
      <div className="mb-3 min-w-[180px]">
        <SearchField
          id="location-scope-tree-search"
          isLoading={isSearchPending}
          onSearch={(value) => {
            const next = value ?? "";
            setSearchInput(next);
            if (!next.trim()) {
              setDebouncedSearchQuery("");
            }
          }}
          loaderColor="rgb(156 163 175)"
          placeholderText="Search locations..."
          includeSetWidth={false}
          outerContainerClassName="flex items-center my-0"
          debounceDelay={0}
        />
      </div>

      <div className="border border-gray-200 rounded-t-md border-b-0 bg-gray-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-700 truncate">
            Locations
          </span>
          <span
            className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 flex-shrink-0"
            aria-label={`${selectedCount} selected`}
          >
            {selectedCount}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          <button
            type="button"
            onClick={handleSelectAllInList}
            className="hover:text-gray-700 hover:underline"
            disabled={allValues.length === 0}
          >
            Select all
          </button>
          <span className="text-gray-400" aria-hidden="true">
            |
          </span>
          <button
            type="button"
            onClick={handleClearAll}
            className="hover:text-gray-700 hover:underline"
          >
            Clear all
          </button>
        </div>
      </div>

      {displayNodes.length === 0 ? (
        <div className="border border-gray-200 rounded-b-md mb-2 py-3 px-3">
          <p className="text-sm text-gray-500 text-center">
            {hasSearch ? "No matching locations." : "No locations available."}
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-b-md mb-2">
          <div
            className="overflow-y-auto px-2 py-2 location-scope-checkbox-tree [&_.react-checkbox-tree]:text-sm [&_.rct-title]:text-sm [&_label]:text-sm"
            style={{ maxHeight: listMaxHeight }}
          >
            <CheckboxTree
              checkModel="all"
              showExpandAll={false}
              iconsClass="fa5"
              icons={treeIcons}
              nodes={displayNodes}
              checked={checked}
              expanded={expanded}
              onCheck={handleCheck}
              onExpand={onExpandedChange}
            />
          </div>
          <div className="border-t border-gray-200 px-3 py-2 flex justify-end">
            <button
              type="button"
              onClick={() => onExpandedChange([])}
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
              disabled={expanded.length === 0}
            >
              Collapse all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationScopeSearchableTree;
